import { Component, OnDestroy, OnInit } from '@angular/core';
import { AlertDialogComponent } from '../../../components/shared/alert-dialog/alert-dialog.component';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ClientDetailsForMatching, SignupService } from '../signup.service';
import { BehaviorSubject, Subscription, forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { PasswordMatchErrorMatcher } from '../../../components/password-matching-validator';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { TermsDialogComponent } from '../../../components/shared/terms-dialog/terms-dialog.component';
import { ParseErrorHandler } from '../../../parse-error-handler';
import { LoginClinicDetails } from '@appyvet/vetbooker-definitions/dist/clinic_details';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { RegistrationService } from '../../../registration/registration.service';
import { RegistrationRequirements } from '@appyvet/vetbooker-definitions/dist/registrations';

@Component({
  selector: 'app-matching',
  templateUrl: './matching.component.html',
  styleUrls: ['./matching.component.scss'],
})
export class MatchingComponent implements OnInit, OnDestroy {
  matchingForm: UntypedFormGroup;
  accountValidationMessages = this.signupService.accountValidationMessages;
  loading$ = this.signupService.loading$;
  matcher = new PasswordMatchErrorMatcher();
  private clinicName: string;
  clinicDetails$ = new BehaviorSubject<LoginClinicDetails>(null);
  private clinicCode: string;
  consent: boolean;
  private passwordRegexp: RegExp = new RegExp(
    '^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{12,}$',
  );
  termsLink: string = environment.VETS_4_PETS ? '' : 'https://';
  showSocialSignIn = environment.VETS_4_PETS;
  private matchingClient: ClientDetailsForMatching;
  private screenWidth: number;
  private routeSub: Subscription;
  private createdUserSub: Subscription;
  private matchUserErrorSub: Subscription;
  private registrationSettingsSub: Subscription;
  requirements: RegistrationRequirements;
  private countryPostcodeConfig: {
    [key: string]: {
      pattern?: RegExp;
      minLength?: number;
      maxLength?: number;
      exactLength?: number;
    };
  } = {
    gb: { minLength: 4, maxLength: 8 },
    us: { pattern: /^\d{5}(-\d{4})?$/, minLength: 5, maxLength: 10 },
    au: { pattern: /^\d{4}$/, exactLength: 4 },
    ie: {
      pattern: /^[A-Za-z0-9]{3}\s?[A-Za-z0-9]{4}$/,
      minLength: 7,
      maxLength: 8,
    },
    nz: { pattern: /^\d{4}$/, exactLength: 4 },
    fi: { pattern: /^\d{5}$/, exactLength: 5 },
    it: { pattern: /^\d{5}$/, exactLength: 5 },
    es: { pattern: /^\d{5}$/, exactLength: 5 },
    ae: { pattern: /^\d{1,6}$/, minLength: 1, maxLength: 6 },
    za: { pattern: /^\d{4}$/, exactLength: 4 },
    nl: { pattern: /^\d{4}\s?[A-Z]{2}$/i, minLength: 6, maxLength: 7 },
  };

  constructor(
    private signupService: SignupService,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private gaService: GoogleAnalyticsService,
    private registrationService: RegistrationService,
  ) {}

  checkPasswords(group: UntypedFormGroup) {
    // here we have the 'passwords' group
    const pass = group.get('password').value;
    const confirmPass = group.get('confirmPassword').value;
    return pass === confirmPass ? null : { mismatch: true };
  }

  goToPassword() {
    this.signupService.currentPage$.next('password');
  }

  ngOnInit(): void {
    this.screenWidth = window.innerWidth;
    window.onresize = () => {
      this.screenWidth = window.innerWidth;
    };
    const passwordValidators = [Validators.required];
    passwordValidators.push(Validators.pattern(this.passwordRegexp));
    this.matchingForm = new UntypedFormGroup(
      {
        email: new UntypedFormControl(null, [
          Validators.required,
          Validators.email,
        ]),
        mobile: new UntypedFormControl(null),
        postcode: new UntypedFormControl(null),
        password: new UntypedFormControl(null, passwordValidators),
        confirmPassword: new UntypedFormControl(null, passwordValidators),
      },
      { validators: this.checkPasswords },
    );
    this.routeSub = this.activatedRoute.data.subscribe((data) => {
      const clinicDetails: LoginClinicDetails = data.clinicDetails;
      if (clinicDetails) {
        this.clinicCode = clinicDetails.clinicCode;
        this.clinicName = clinicDetails.clinicName;

        // Fire both in parallel — getRegistrationSettings alongside clinicDetails
        this.registrationSettingsSub = forkJoin({
          clinicDetails: new BehaviorSubject(clinicDetails).pipe(take(1)),
          settings: this.registrationService
            .getRegistrationSettings(clinicDetails.clinicCode)
            .pipe(take(1)),
        }).subscribe(({ clinicDetails: cd, settings }) => {
          this.requirements = settings?.requirements ?? null;
          // Merge requirements.mobile into clinicDetails so the template reacts
          const mergedClinicDetails: LoginClinicDetails = {
            ...cd,
            smsRegistrationAllowed:
              cd.smsRegistrationAllowed || !!this.requirements?.mobile,
            requirePostcode:
              cd.requirePostcode || !!this.requirements?.postcode,
          };
          this.clinicDetails$.next(mergedClinicDetails);
          this.applyRequirementsValidators(
            this.requirements,
            mergedClinicDetails,
          );
        });
      }
    });
    this.matchUserErrorSub = this.signupService.matchUserError$.subscribe(
      (error) => {
        if (error) {
          const parsedError = ParseErrorHandler.handleParseError(error);
          this.dialog.open(AlertDialogComponent, {
            maxWidth: this.screenWidth > 400 ? '80vw' : '100vw',
            data: {
              title: 'Error Registering',
              message: parsedError.message,
              confirmationText: 'Got it',
            },
          });
          this.loading$.next(false);
        }
      },
    );
  }

  private applyRequirementsValidators(
    requirements: RegistrationRequirements | null,
    clinicDetails: LoginClinicDetails,
  ): void {
    // Mobile validators
    if (requirements?.mobile || clinicDetails.smsRegistrationAllowed) {
      this.matchingForm
        .get('mobile')
        .setValidators([
          Validators.required,
          Validators.pattern(clinicDetails.country.mobileRegex),
        ]);
    } else {
      this.matchingForm.get('mobile').clearValidators();
    }

    // Postcode validators
    if (requirements?.postcode || clinicDetails.requirePostcode) {
      const postcodeValidators = this.buildPostcodeValidators(clinicDetails);
      this.matchingForm.get('postcode').setValidators(postcodeValidators);

      // Update postcode error messages dynamically based on country
      this.accountValidationMessages.postcode =
        this.getPostcodeValidationMessages(clinicDetails);
    } else {
      this.matchingForm.get('postcode').clearValidators();
    }

    this.matchingForm.get('mobile').updateValueAndValidity();
    this.matchingForm.get('postcode').updateValueAndValidity();
  }

  private buildPostcodeValidators(clinicDetails: LoginClinicDetails): any[] {
    const validators: any[] = [Validators.required];

    // Get country code from clinic details (adjust property name as needed)
    const countryCode =
      (clinicDetails?.country as any)?.code?.toLowerCase() ||
      (clinicDetails?.country as any)?.countryCode?.toLowerCase();

    const config = this.countryPostcodeConfig[countryCode];

    if (config) {
      // Use country-specific pattern, or fallback to clinic's alphanumeric regex
      if (config.pattern) {
        validators.push(Validators.pattern(config.pattern));
      } else if (clinicDetails.country?.alphanumericRegex) {
        validators.push(
          Validators.pattern(clinicDetails.country.alphanumericRegex),
        );
      }

      if (config.exactLength) {
        validators.push(Validators.minLength(config.exactLength));
        validators.push(Validators.maxLength(config.exactLength));
      } else {
        if (config.minLength) {
          validators.push(Validators.minLength(config.minLength));
        }
        if (config.maxLength) {
          validators.push(Validators.maxLength(config.maxLength));
        }
      }
    } else {
      // Fallback to current behavior
      if (clinicDetails.country?.alphanumericRegex) {
        validators.push(
          Validators.pattern(clinicDetails.country.alphanumericRegex),
        );
      }
      validators.push(Validators.minLength(4), Validators.maxLength(8));
    }

    return validators;
  }

  private getPostcodeValidationMessages(
    clinicDetails: LoginClinicDetails,
  ): { type: string; message: string }[] {
    const countryCode =
      (clinicDetails?.country as any)?.code?.toLowerCase() ||
      (clinicDetails?.country as any)?.countryCode?.toLowerCase();

    const config = this.countryPostcodeConfig[countryCode];

    const messages: { type: string; message: string }[] = [
      { type: 'required', message: 'Postcode is required' },
      {
        type: 'pattern',
        message: `Oops, looks like we didn't recognize that as a postcode`,
      },
    ];

    if (!config) {
      messages.push({
        type: 'maxlength',
        message: 'Postcode must be less than 8 characters',
      });
      messages.push({
        type: 'minlength',
        message: 'Postcode must be at least 4 characters',
      });
      return messages;
    }

    if (config.exactLength) {
      messages.push({
        type: 'minlength',
        message: `Postcode must be exactly ${config.exactLength} characters`,
      });
      messages.push({
        type: 'maxlength',
        message: `Postcode must be exactly ${config.exactLength} characters`,
      });
    } else {
      if (config.minLength) {
        messages.push({
          type: 'minlength',
          message: `Postcode must be at least ${config.minLength} characters`,
        });
      }
      if (config.maxLength) {
        messages.push({
          type: 'maxlength',
          message: `Postcode must be less than ${config.maxLength + 1} characters`,
        });
      }
    }

    return messages;
  }

  startRegistration() {
    this.loading$.next(true);
    const matchingClient: ClientDetailsForMatching = {
      email: this.matchingForm.get('email').value,
      mobile: this.matchingForm.get('mobile').value,
      postcode: this.matchingForm.get('postcode').value,
      password: this.matchingForm.get('password').value,
    };
    this.matchingClient = matchingClient;
    this.signupService.startRegistration(matchingClient);
    this.gaService.event(
      'Client Matching Search',
      'Client Matching',
      this.clinicName,
    );
  }

  showTerms() {
    this.dialog.open(TermsDialogComponent);
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.createdUserSub?.unsubscribe();
    this.matchUserErrorSub?.unsubscribe();
    this.registrationSettingsSub?.unsubscribe();
  }
}
